import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Row,
  Placeholder,
} from "react-bootstrap";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import uploadMultipleFiles from "../../../../utilities/uploadMultipleFiles";
import { catchAsync, checkResponse } from "../../../../utilities/utilities";
import {
  HOTELLIST,
  ROOMADD,
  ROOMESSENTAILLIST,
  ROOMLIST,
  ROOMTYPE,
  ROOMUPDATE,
} from "../../../../services/ApiCalls";
import Loader from "../../../../components/Common/Loader";
import { useTranslation } from "react-i18next";

const optionalNumberSchema = (label) =>
  z.preprocess(
    (value) =>
      value === "" ||
      value === null ||
      value === undefined ||
      Number.isNaN(value)
        ? undefined
        : Number(value),
    z
      .number({ invalid_type_error: `${label} must be a number` })
      .min(0, `${label} cannot be negative`)
      .optional()
  );

const pricingDetailSchema = z
  .object({
    _id: z.string().optional(),
    pricingType: z.enum(["hour", "day"], {
      required_error: "Pricing type is required",
    }),
    duration: z.coerce
      .number()
      .int("Duration must be an integer")
      .min(1, "Duration is required"),
    weekdayPrice: z.coerce.number().min(1, "Weekday price is required"),
    saturdayPrice: optionalNumberSchema("Saturday price"),
    sundayPrice: optionalNumberSchema("Sunday price"),
    isActive: z.boolean(),
    checkInTime: z.object({
      from: z.string().nonempty("Available from time is required"),
      to: z.string().nonempty("Available till time is required"),
    }),
  })
  .superRefine((price, ctx) => {
    if (price.pricingType === "day" && price.duration !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["duration"],
        message: "Day pricing duration must be 1",
      });
    }
  });

const defaultCheckInTime = {
  from: "09:00",
  to: "18:00",
};

const roomSchema = z.object({
  hotelId: z.string().nonempty("Hotel is required"),
  amenities: z.array(z.string()).min(1, "Select at least one amenity"),
  roomImages: z.array(z.string()).min(1, "At least one image is required"),
  unavailableOnDates: z.array(z.string()).optional(),
  bedType: z.string().nonempty("Bed Type is required"),
  type: z.string().nonempty("Room type is required"),
  status: z.enum(["Available", "Occupied", "Unavailable","Maintenance","Active","Inactive"], {
    required_error: "Status is required",
  }),
  maxOccupancy: z.coerce
    .number()
    .int("Max occupancy must be an integer")
    .min(1, "Number of people must be greater than 0"),

  length: z.coerce
    .number()
    .int("Length must be an integer")
    .min(1, "Length is required"),

  width: z.coerce
    .number()
    .int("Width must be an integer")
    .min(1, "Width is required"),
  totalRoom: optionalNumberSchema("Total rooms"),
  noOfRoom: optionalNumberSchema("No. of rooms"),
  availableRoom: optionalNumberSchema("Available rooms"),
  maxChilOccupancy: optionalNumberSchema("Child occupancy"),
  extraAdultPrice: optionalNumberSchema("Extra adult price"),
  extraChildPrice: optionalNumberSchema("Extra child price"),
  avgRating: optionalNumberSchema("Average rating"),
  housekeepingNotes: z.string().optional(),
  pricingDetail: z
    .array(pricingDetailSchema)
    .min(1, "Enter at least one pricing detail"),
});

const defaultPricingDetail = [
  {
    pricingType: "hour",
    duration: 1,
    weekdayPrice: 0,
    saturdayPrice: "",
    sundayPrice: "",
    isActive: true,
    checkInTime: { ...defaultCheckInTime },
  },
];

const allowedPricingTypes = ["hour", "day"];

const normalizePricingType = (price = {}) => {
  const pricingType = (price.pricingType || price.durationType || "hour")
    .toString()
    .toLowerCase();

  return allowedPricingTypes.includes(pricingType) ? pricingType : "hour";
};

const optionalPayloadValue = (value) =>
  value === "" || value === null || value === undefined || Number.isNaN(value)
    ? undefined
    : value;

const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}/;

const normalizeDateValue = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const dateOnlyMatch = value.match(dateOnlyRegex);
    if (dateOnlyMatch) return dateOnlyMatch[0];
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

const normalizeDateList = (dates = []) =>
  Array.from(new Set((dates || []).map(normalizeDateValue).filter(Boolean))).sort();

const getLegacyPrice = (price = {}) =>
  optionalPayloadValue(price.weekdayPrice) ??
  optionalPayloadValue(price.finalPrice) ??
  optionalPayloadValue(price.basePrice) ??
  0;

const normalizeCheckInTime = (price = {}) => ({
  from:
    price.checkInTime?.from ||
    price.checkInFrom ||
    price.checkInTimeFrom ||
    defaultCheckInTime.from,
  to:
    price.checkInTime?.to ||
    price.checkInTo ||
    price.checkInTimeTo ||
    defaultCheckInTime.to,
});

const buildPricingDetail = (pricingDetail = []) => {
  const groupedPricing = new Map();

  pricingDetail.forEach((price) => {
    const pricingType = normalizePricingType(price);
    const duration = pricingType === "day" ? 1 : Number(price.duration || 1);
    const key = `${pricingType}-${duration}`;
    const current = groupedPricing.get(key) || {
      pricingType,
      duration,
      weekdayPrice: undefined,
      saturdayPrice: undefined,
      sundayPrice: undefined,
      isActive:
        typeof price.isActive === "boolean" ? price.isActive : false,
      checkInTime: normalizeCheckInTime(price),
    };

    const rowWeekdayPrice = optionalPayloadValue(price.weekdayPrice);
    const legacyWeekendPrice = optionalPayloadValue(price.weekendPrice);
    const legacyPrice = getLegacyPrice(price);

    if (rowWeekdayPrice !== undefined) {
      current.weekdayPrice = Number(rowWeekdayPrice);
    } else if (price.pricingLabel === "weekend") {
      current.saturdayPrice = Number(legacyPrice);
      current.sundayPrice = Number(legacyPrice);
    } else {
      current.weekdayPrice = Number(legacyPrice);
    }

    for (const day of ["saturdayPrice", "sundayPrice"]) {
      const dayPrice = optionalPayloadValue(price[day]) ?? legacyWeekendPrice;
      if (dayPrice !== undefined) {
        current[day] = Number(dayPrice);
      }
    }

    if (typeof price.isActive === "boolean") {
      current.isActive = price.isActive;
    }

    current.checkInTime = normalizeCheckInTime(price);
    if (price._id) {
      current._id = price._id;
    }

    groupedPricing.set(key, current);
  });

  return Array.from(groupedPricing.values()).map((price) => {
    const weekdayPrice = Number(price.weekdayPrice || 0);
    const pricingPayload = {
      pricingType: price.pricingType,
      duration: price.pricingType === "day" ? 1 : Number(price.duration || 1),
      weekdayPrice,
      isActive: price.isActive,
      checkInTime: normalizeCheckInTime(price),
    };

    if (price._id) {
      pricingPayload._id = price._id;
    }

    for (const day of ["saturdayPrice", "sundayPrice"]) {
      const dayPrice = optionalPayloadValue(price[day]);
      if (dayPrice !== undefined) {
        pricingPayload[day] = Number(dayPrice);
      }
    }

    return pricingPayload;
  });
};

const buildRoomPayload = (data, id) => {
  const totalRoom = optionalPayloadValue(data.totalRoom ?? data.noOfRoom);

  const payload = {
    ...(id && { _id:id }),
    hotelId: data.hotelId,
    amenities: data.amenities,
    type: data.type,
    totalRoom,
    noOfRoom: optionalPayloadValue(data.noOfRoom) ?? totalRoom,
    availableRoom: optionalPayloadValue(data.availableRoom),
    images: data.roomImages?.map((image) => image?._id || image),
    maxOccupancy: data.maxOccupancy,
    maxChilOccupancy: optionalPayloadValue(data.maxChilOccupancy),
    extraAdultPrice: optionalPayloadValue(data.extraAdultPrice),
    extraChildPrice: optionalPayloadValue(data.extraChildPrice),
    length: data.length,
    width: data.width,
    bedType: data.bedType,
    status: data.status,
    unavailableOnDates: normalizeDateList(data.unavailableOnDates),
    pricingDetail: buildPricingDetail(data.pricingDetail),
    avgRating: optionalPayloadValue(data.avgRating),
    housekeepingNotes:data.housekeepingNotes
  };

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined)
  );
};

const AddRoom = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page") || 1;

  const [imagePreviews, setImagePreviews] = useState([]);
  const [imgloader, setImgLoader] = useState(false);
  const [roomAmenities, setRoomAmenities] = useState([]);
  const [hotelAdminData, setHotelData] = useState([]);
  const [roomTypeData, setRoomTypeData] = useState([]);
  const [hotelDetailData, setHotelDetailData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [selectedUnavailableDate, setSelectedUnavailableDate] = useState("");

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(roomSchema),
    defaultValues: {
      hotelId: "",
      amenities: [],
      floorNumber: "",
      roomNumber: "",
      roomImages: [],
      bedType: "Queen",
      type: "",
      width: 15,
      length: 10,
      maxOccupancy: "",
      maxChilOccupancy: "",
      totalRoom: "",
      noOfRoom: "",
      availableRoom: "",
      extraAdultPrice: "",
      extraChildPrice: "",
      avgRating: "",
      status: "Available",
      unavailableOnDates: [],
      housekeepingNotes: "",
      pricingDetail: defaultPricingDetail,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "pricingDetail",
  });

  const getAmenityData = catchAsync(async () => {
    const [roomRes, hotelRes, roomTypeRes] = await Promise.all([
      ROOMESSENTAILLIST({ limit: 100, page: 1, status: "Active" }),
      HOTELLIST({ limit: 100, page: 1, status: "Active" }),
      ROOMTYPE({ limit: 100, page: 1, status: "Active" }),
    ]);

    checkResponse({ res: roomRes, setData: setRoomAmenities });
    checkResponse({ res: hotelRes, setData: setHotelData });
    checkResponse({ res: roomTypeRes, setData: setRoomTypeData });
  });

  const getData = catchAsync(async () => {
    setLoader(true);
    const res = await ROOMLIST({ id });
    const success = checkResponse({
      res,
      setData: setHotelDetailData,
      setLoader,
    });

    if (success && res?.data?.data) {
      const data = res.data.data;
      console.log(data, "data===<>come");
      const roomImages = data.roomImages || data.images || [];
      setImagePreviews(roomImages);
      const pricingDetail = data.pricingDetail?.length
        ? data.pricingDetail
        : data.pricing?.length
        ? data.pricing
        : defaultPricingDetail;

      reset({
        hotelId: data.hotelId?._id || "",
        amenities: data.amenities?.map((a) => a._id) || [],
        floorNumber: data.floorNumber || "",
        roomNumber: data.roomNumber || "",
        bedType: data.bedType || "Queen",
        type: data.type?._id || "",
        status: data.status || "Available",
        maxOccupancy: data?.maxOccupancy || "",
        maxChilOccupancy:
          data?.maxChilOccupancy || data?.maxChildOccupancy || "",
        totalRoom: data?.totalRoom ?? data?.noOfRoom ?? "",
        noOfRoom: data?.noOfRoom || "",
        availableRoom: data?.availableRoom || "",
        extraAdultPrice: data?.extraAdultPrice || "",
        extraChildPrice: data?.extraChildPrice || "",
        avgRating: data?.avgRating || "",
        length: data?.length || 10,
        width: data?.width || 15,
        unavailableOnDates: normalizeDateList(data?.unavailableOnDates),
        housekeepingNotes: data.housekeepingNotes || "",
        roomImages: roomImages?.map((image) => image?._id || image) || [],
        pricingDetail: buildPricingDetail(pricingDetail),
      });
    }
  }, setLoader);

  useEffect(() => {
    getAmenityData();
    if (id) getData();
  }, [id]);

  const handleCheckbox = (field, value, checked) => {
    const currentValues = watch("amenities") || [];
    const updated = checked
      ? [...currentValues, value]
      : currentValues.filter((id) => id !== value);
    setValue("amenities", updated, { shouldValidate: true });
  };

  const handleImageChange = catchAsync(async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setImgLoader(true);
    const res = await uploadMultipleFiles(files);
    setImgLoader(false);

    if (!Array.isArray(res) || !res.length) return;

    const uploadedIds = res.map((image) => image?._id).filter(Boolean);
    const updated = [...(watch("roomImages") || []), ...uploadedIds];
    setValue("roomImages", updated, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setImagePreviews((prev) => [...prev, ...res]);
  }, setImgLoader);

  const handleRemoveImage = (index) => {
    const updated = (watch("roomImages") || []).filter((_, i) => i !== index);
    setValue("roomImages", updated, { shouldValidate: true });
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddUnavailableDate = () => {
    if (!selectedUnavailableDate) return;

    const updated = normalizeDateList([
      ...(watch("unavailableOnDates") || []),
      selectedUnavailableDate,
    ]);

    setValue("unavailableOnDates", updated, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setSelectedUnavailableDate("");
  };

  const handleRemoveUnavailableDate = (date) => {
    const updated = (watch("unavailableOnDates") || []).filter(
      (unavailableDate) => unavailableDate !== date
    );

    setValue("unavailableOnDates", updated, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const submitHandler = catchAsync(async (data) => {
    const body = buildRoomPayload(data, id);
    console.log(body, "body ====<>");
    const res = await (id ? ROOMUPDATE(body) : ROOMADD(body));
    const success = checkResponse({ res, showSuccess: true });
    if (success) navigate(`/room?page=${page}`);
  });

  const handleCancel = () => navigate(`/room?page=${page}`);

  console.log(hotelAdminData, "hotelAdminData===");
  const pricingValues = watch("pricingDetail") || [];
  const unavailableOnDates = watch("unavailableOnDates") || [];

  return (
    <>
      {loader && <Loader />}
      <section className="addRoom position-relative py-3">
        <Container>
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center gap-10">
                <Link
                  to={`/room?page=${page}`}
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#E8307A"
                      stroke="#E8307A"
                      strokeWidth="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold text-dark text-capitalize">
                  {id ? t("manageRoom.editRoom") : t("manageRoom.addRoom")}
                </h4>
              </div>
            </Col>

            <Col lg="12" className="my-2">
              <div
                className="formWrpper px-lg-5 p-md-4 p-3 rounded"
                style={{ background: "#EEEEEE" }}
              >
                <Form onSubmit={handleSubmit(submitHandler)}>
                  <Row className="justify-content-between">
                    <Col lg="6" className="my-2">
                      <label>{t("manageRoom.selectHotel")} </label>
                      <select className="form-control" {...register("hotelId")}>
                        <option value="">-- Select Hotel --</option>
                        {hotelAdminData?.hotelList?.map((admin) => (
                          <option key={admin._id} value={admin._id}>
                            {admin.hotelName}
                          </option>
                        ))}
                      </select>

                      {errors?.hotelId && (
                        <p className="text-danger">{errors.hotelId.message}</p>
                      )}
                    </Col>

                    {/* <Col lg="6" className="my-2">
                      <label>{t("manageRoom.columns.floorNumber")}</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("floorNumber", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.floorNumber && (
                        <p className="text-danger">
                          {errors.floorNumber.message}
                        </p>
                      )}
                    </Col> */}

                    {/* <Col lg="6" className="my-2">
                      <label>{t("manageRoom.roomNumber")}</label>
                      <input
                        type="text"
                        className="form-control"
                        {...register("roomNumber")}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.roomNumber && (
                        <p className="text-danger">
                          {errors.roomNumber.message}
                        </p>
                      )}
                    </Col> */}

                    <Col lg="6" className="my-2">
                      <label>{t("manageRoom.totalRoom", "Total Rooms")}</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("totalRoom", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.totalRoom && (
                        <p className="text-danger">
                          {errors.totalRoom.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>
                        {t("manageRoom.availableRoom", "Available Rooms")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("availableRoom", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.availableRoom && (
                        <p className="text-danger">
                          {errors.availableRoom.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("manageRoom.columns.bedType", "Bed Type")}</label>
                      <Form.Select {...register("bedType")}>
                        <option value="">Select Bed Type</option>
                        <option value="Queen">Queen</option>
                        <option value="Twin">Twin</option>
                        <option value="King">King</option>
                      </Form.Select>
                      {errors.bedType && (
                        <p className="text-danger">
                          {errors.bedType.message}
                        </p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>
                        {t("manageRoom.numberOfPeople", "People Allowed")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("maxOccupancy", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.maxOccupancy && (
                        <p className="text-danger">
                          {errors.maxOccupancy.message}
                        </p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>Children Allowed</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("maxChilOccupancy", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.maxChilOccupancy && (
                        <p className="text-danger">
                          {errors.maxChilOccupancy.message}
                        </p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>Extra Adult Price</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("extraAdultPrice", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.extraAdultPrice && (
                        <p className="text-danger">
                          {errors.extraAdultPrice.message}
                        </p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>Extra Child Price</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("extraChildPrice", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.extraChildPrice && (
                        <p className="text-danger">
                          {errors.extraChildPrice.message}
                        </p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>
                        {t("manageRoom.roomLength", "Room Length (meter)")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("length", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.length && (
                        <p className="text-danger">{errors.length.message}</p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>
                        {t("manageRoom.roomWidth", "Room Width (meter)")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("width", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.width && (
                        <p className="text-danger">{errors.width.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label> {t("manageRoom.roomType")} </label>
                      <select className="form-control" {...register("type")}>
                        <option value="">-- Select Room Type --</option>
                        {roomTypeData?.map((admin) => (
                          <option key={admin._id} value={admin._id}>
                            {admin.name}
                          </option>
                        ))}
                      </select>
                      {errors?.type && (
                        <p className="text-danger">{errors.type.message}</p>
                      )}
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>{t("manageRoom.columns.status")}</label>
                      <Form.Select {...register("status")}>
                        <option value="Available">Available</option>
            <option value="Unavailable">Unavailable</option>

                        <option value="Occupied">Occupied</option>
                        <option value="Maintenance">Maintenance</option>
                                                {/* <option value="Active">Active</option>
                                                  <option value="Inactive">In Active</option> */}

                      </Form.Select>
                    </Col>
                    <Col lg="6" className="my-2">
                      <label>Average Rating</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        step="0.1"
                        {...register("avgRating", {
                          valueAsNumber: true,
                          min: 0,
                          max:5
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.avgRating && (
                        <p className="text-danger">
                          {errors.avgRating.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="12" className="my-2">
                      <label>{t("manageRoom.housekeepingNotes")}</label>
                      <textarea
                        rows={3}
                        className="form-control"
                        {...register("housekeepingNotes")}
                      />
                    </Col>

                    <Col lg="12" className="my-2">
                      <label className="fw-semibold">
                        {t("manageRoom.unavailableOnDates", "Unavailable Dates")}
                      </label>
                      <Row className="align-items-end">
                        <Col lg="4" md="6" className="my-2">
                          <input
                            type="date"
                            className="form-control"
                            value={selectedUnavailableDate}
                            onChange={(e) =>
                              setSelectedUnavailableDate(e.target.value)
                            }
                          />
                        </Col>
                        <Col lg="2" md="3" className="my-2">
                          <Button
                            type="button"
                            variant="primary"
                            onClick={handleAddUnavailableDate}
                            disabled={!selectedUnavailableDate}
                          >
                            Add Date
                          </Button>
                        </Col>
                      </Row>

                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {unavailableOnDates.length ? (
                          unavailableOnDates.map((date) => (
                            <span
                              key={date}
                              className="badge bg-secondary d-inline-flex align-items-center gap-2 px-3 py-2"
                            >
                              {date}
                              <button
                                type="button"
                                className="btn-close btn-close-white"
                                style={{ fontSize: 8 }}
                                aria-label={`Remove ${date}`}
                                onClick={() => handleRemoveUnavailableDate(date)}
                              />
                            </span>
                          ))
                        ) : (
                          <p className="text-muted mb-0">
                            No unavailable dates selected
                          </p>
                        )}
                      </div>
                      {errors.unavailableOnDates && (
                        <p className="text-danger">
                          {errors.unavailableOnDates.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="12" className="my-2">
                      <label className="fw-semibold">Room Amenities</label>
                      <div
                        className="d-flex flex-wrap gap-3 p-2 border rounded"
                        style={{ maxHeight: "200px", overflowY: "auto" }}
                      >
                        {roomAmenities?.length > 0
                          ? roomAmenities.map((amenity) => (
                              <div
                                key={amenity._id}
                                className="d-flex align-items-center"
                                style={{ minWidth: "150px" }}
                              >
                                <Form.Check
                                  type="checkbox"
                                  label={amenity.name}
                                  checked={watch("amenities")?.includes(
                                    amenity._id
                                  )}
                                  onChange={(e) =>
                                    handleCheckbox(
                                      "amenities",
                                      amenity._id,
                                      e.target.checked
                                    )
                                  }
                                />
                              </div>
                            ))
                          : Array.from({ length: 6 }).map((_, idx) => (
                              <Placeholder key={idx} xs={8} bg="secondary" />
                            ))}
                      </div>
                      {errors.amenities && (
                        <p className="text-danger">
                          {errors.amenities.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="12" className="my-2">
                      <label className="fw-bold text-muted ps-2 m-0">
                        Upload Images
                      </label>
                      <div
                        className="border border-2 border-dashed rounded d-flex flex-column align-items-center justify-content-center p-4 bg-light"
                        style={{ cursor: "pointer", minHeight: "140px" }}
                        onClick={() =>
                          document.getElementById("imageUploadInput").click()
                        }
                      >
                        <i className="bi bi-cloud-upload fs-1 text-muted"></i>
                        <p className="m-0 text-muted">
                          Click or Drag & Drop to Upload
                        </p>
                        <input
                          id="imageUploadInput"
                          type="file"
                          accept="image/*"
                          multiple
                          className="d-none"
                          onChange={handleImageChange}
                        />
                      </div>

                      {errors.roomImages && (
                        <p className="text-danger mt-1">
                          {errors.roomImages.message}
                        </p>
                      )}

                      <div className="d-flex flex-wrap gap-2 mt-3">
                        {imgloader ? (
                          <div
                            style={{ height: 120, width: 120 }}
                            className="d-flex align-items-center justify-content-center border rounded"
                          >
                            Loading...
                          </div>
                        ) : (
                          imagePreviews.map((image, i) => (
                            <div
                              key={i}
                              style={{
                                width: 120,
                                height: 120,
                                position: "relative",
                              }}
                              className="border rounded overflow-hidden shadow-sm"
                            >
                              <img
                                src={image?.link || image}
                                alt={`preview-${i}`}
                                className="w-100 h-100 object-fit-cover"
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 rounded-circle"
                                onClick={() => handleRemoveImage(i)}
                              >
                                ✕
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </Col>

                    <Col lg="12" className="my-2">
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                        <label className="fw-semibold m-0">
                          Pricing Detail
                        </label>
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          onClick={() =>
                            append({
                              pricingType: "hour",
                              duration: 1,
                              weekdayPrice: 0,
                              saturdayPrice: "",
                              sundayPrice: "",
                              isActive: true,
                              checkInTime: { ...defaultCheckInTime },
                            })
                          }
                        >
                          Add Pricing
                        </Button>
                      </div>

                      {fields.map((field, index) => {
                        const selectedPricingType =
                          pricingValues?.[index]?.pricingType || "hour";
                        const isDayPricing = selectedPricingType === "day";

                        return (
                          <div
                            key={field.id}
                            className="border rounded p-3 mb-3 bg-light"
                          >
                            <Row className="align-items-end">
                              <Col lg="2" md="4" className="my-2">
                                <label>Pricing Type</label>
                                <Form.Select
                                  {...register(
                                    `pricingDetail.${index}.pricingType`,
                                    {
                                      onChange: (e) => {
                                        if (e.target.value === "day") {
                                          setValue(
                                            `pricingDetail.${index}.duration`,
                                            1,
                                            { shouldValidate: true }
                                          );
                                        }
                                      },
                                    }
                                  )}
                                >
                                  <option value="hour">Hour</option>
                                  <option value="day">Day</option>
                                </Form.Select>
                                {errors.pricingDetail?.[index]
                                  ?.pricingType && (
                                  <p className="text-danger">
                                    {
                                      errors.pricingDetail[index].pricingType
                                        .message
                                    }
                                  </p>
                                )}
                              </Col>

                              <Col lg="2" md="4" className="my-2">
                                <label>Duration</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  min={1}
                                  {...register(
                                    `pricingDetail.${index}.duration`,
                                    { valueAsNumber: true }
                                  )}
                                  readOnly={isDayPricing}
                                  onKeyDown={(e) =>
                                    e.key === "-" && e.preventDefault()
                                  }
                                />
                                {errors.pricingDetail?.[index]?.duration && (
                                  <p className="text-danger">
                                    {
                                      errors.pricingDetail[index].duration
                                        .message
                                    }
                                  </p>
                                )}
                              </Col>

                              <Col lg="2" md="4" className="my-2">
                                <label>Weekday Price</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  min={0}
                                  {...register(
                                    `pricingDetail.${index}.weekdayPrice`,
                                    { valueAsNumber: true }
                                  )}
                                  onKeyDown={(e) =>
                                    e.key === "-" && e.preventDefault()
                                  }
                                />
                                {errors.pricingDetail?.[index]?.weekdayPrice && (
                                  <p className="text-danger">
                                    {
                                      errors.pricingDetail[index].weekdayPrice
                                        .message
                                    }
                                  </p>
                                )}
                              </Col>

                              <Col lg="2" md="4" className="my-2">
                                <label>Saturday Price</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  min={0}
                                  {...register(
                                    `pricingDetail.${index}.saturdayPrice`,
                                    { valueAsNumber: true }
                                  )}
                                  onKeyDown={(e) =>
                                    e.key === "-" && e.preventDefault()
                                  }
                                />
                                {errors.pricingDetail?.[index]?.saturdayPrice && (
                                  <p className="text-danger">
                                    {
                                      errors.pricingDetail[index].saturdayPrice
                                        .message
                                    }
                                  </p>
                                )}
                              </Col>

                              <Col lg="2" md="4" className="my-2">
                                <label>Sunday Price</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  min={0}
                                  {...register(
                                    `pricingDetail.${index}.sundayPrice`,
                                    { valueAsNumber: true }
                                  )}
                                  onKeyDown={(e) =>
                                    e.key === "-" && e.preventDefault()
                                  }
                                />
                                {errors.pricingDetail?.[index]?.sundayPrice && (
                                  <p className="text-danger">
                                    {
                                      errors.pricingDetail[index].sundayPrice
                                        .message
                                    }
                                  </p>
                                )}
                              </Col>

                              <Col lg="2" md="4" className="my-2">
                                <label>
                                  {t("manageRoom.availableFrom", "Available From")}
                                </label>
                                <input
                                  type="time"
                                  className="form-control"
                                  {...register(
                                    `pricingDetail.${index}.checkInTime.from`
                                  )}
                                />
                                {errors.pricingDetail?.[index]?.checkInTime
                                  ?.from && (
                                  <p className="text-danger">
                                    {
                                      errors.pricingDetail[index].checkInTime
                                        .from.message
                                    }
                                  </p>
                                )}
                              </Col>

                              <Col lg="2" md="4" className="my-2">
                                <label>
                                  {t("manageRoom.availableTo", "Available Till")}
                                </label>
                                <input
                                  type="time"
                                  className="form-control"
                                  {...register(
                                    `pricingDetail.${index}.checkInTime.to`
                                  )}
                                />
                                {errors.pricingDetail?.[index]?.checkInTime
                                  ?.to && (
                                  <p className="text-danger">
                                    {
                                      errors.pricingDetail[index].checkInTime
                                        .to.message
                                    }
                                  </p>
                                )}
                              </Col>

                              <Col lg="2" md="4" className="my-2">
                                <Form.Check
                                  type="checkbox"
                                  label="Active"
                                  {...register(
                                    `pricingDetail.${index}.isActive`
                                  )}
                                />
                              </Col>

                              <Col lg="12" className="my-2">
                                <Button
                                  type="button"
                                  variant="danger"
                                  size="sm"
                                  onClick={() => remove(index)}
                                  disabled={fields.length === 1}
                                >
                                  Remove
                                </Button>
                              </Col>
                            </Row>
                          </div>
                        );
                      })}
                      {errors.pricingDetail?.message && (
                        <p className="text-danger">
                          {errors.pricingDetail.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="12" className="my-2">
                      <div className="d-flex align-items-center justify-content-center gap-10">
                        <Button
                          type="button"
                          onClick={handleCancel}
                          className="commonBtn GreyBtn"
                        >
                          Cancel
                        </Button>
                        <Button type="submit" className="commonBtn">
                          Submit
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AddRoom;
